// src/components/BlogPreview.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchBlogPosts } from '../services/apiService';

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  let currentIndex = array.length, randomIndex;
  while (currentIndex !== 0) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
  }
  return array;
};

const truncateText = (html, wordLimit) => {
  if (!html) return '';
  const text = html.replace(/<[^>]*>/g, '');
  const words = text.split(/\s+/);
  return words.length <= wordLimit ? text : words.slice(0, wordLimit).join(' ') + '…';
};

const BlogPreview = ({ shuffle = false }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getPosts = async () => {
      try {
        // ✅ OPTIMIZATION: Fetch only 4 posts from the API
        const { posts: allPosts } = await fetchBlogPosts({ page: 1, perPage: 4 });
        
        if (shuffle) {
          setPosts(shuffleArray(allPosts));
        } else {
          setPosts(allPosts);
        }
      } catch (err) {
        setError('Failed to fetch blog posts. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    getPosts();
  }, [shuffle]);
  // ... (keep loading and error states exactly as they were)
  if (loading) { return <section className="py-24 sm:py-32"><div className="mx-auto max-w-7xl px-6 lg:px-8 text-center"><h2 className="text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">Loading latest insights...</h2></div></section>; }
  if (error) { return <section className="py-24 sm:py-32"><div className="mx-auto max-w-7xl px-6 lg:px-8 text-center"><h2 className="text-3xl font-bold tracking-tight text-red-400 sm:text-4xl">{error}</h2></div></section>; }

  return (
    <section className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-dark-text-primary sm:text-4xl">
            Latest from the Logbook
          </h2>
          <p className="mt-4 text-lg text-dark-text-secondary">
            Insights on AI, social media strategy, and building a brand that lasts.
          </p>
        </div>
        <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 sm:mt-20 md:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-4">
          {posts.map((post) => (
            <Link 
              key={post.id} 
              to={`/blog/${post.slug}`} 
              className="group flex flex-col overflow-hidden rounded-2xl bg-dark-card/40 border border-white/10 transform-gpu transition-all duration-300 ease-in-out hover:scale-105 hover:border-dark-accent-start/50 hover:shadow-2xl hover:shadow-dark-accent-start/10"
            >
              <div className="aspect-square">
                <img 
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" 
                  src={post.featuredMedia?.source_url || 'https://via.placeholder.com/400x400'} 
                  alt={post.title}
                />
              </div>
              <div className="flex flex-1 flex-col justify-between p-6">
                <div className="flex-1">
                  {post.categories && post.categories.length > 0 && (
                     <p className="text-sm font-medium text-dark-accent-start">{post.categories[0].name}</p>
                  )}
                  <h3 className="mt-2 text-xl font-semibold text-dark-text-primary group-hover:text-white">{post.title}</h3>
                  <p className="mt-3 text-sm text-dark-text-secondary">{truncateText(post.excerpt, 18)}</p>
                </div>
                <div className="mt-6 flex items-center">
                  <div className="flex-shrink-0">
                    <span className="sr-only">{post.author.name}</span>
                    {post.author.avatarUrl ? (
                      <img className="h-10 w-10 rounded-full" src={post.author.avatarUrl} alt={post.author.name} />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-dark-card flex items-center justify-center text-dark-text-primary font-bold">{post.author.name.charAt(0)}</div>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-semibold text-dark-text-primary">{post.author.name}</p>
                    <div className="flex space-x-1 text-xs text-dark-text-secondary/70">
                      <time dateTime={post.dateTime}>{new Date(post.dateTime).toLocaleDateString()}</time>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="text-center mt-16">
            <Link to="/blog" className="text-sm font-semibold text-dark-text-primary hover:text-white">
                View all posts &rarr;
            </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;